import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";
import { WorkspaceShellPathSchema } from "./schemas";
import { InviteEmailSchema } from "./workspace-user-schemas";

/**
 * A support request: what a person can say about the tool, applet, or portal page they are on, and
 * what may travel with it.
 *
 * Everything the message reports about *context* — who sent it, which workspace, which tool, which
 * deployed version — is resolved by the Worker from trusted platform state. None of it appears
 * here, because a schema for it would be a schema for something the client is allowed to say. The
 * client supplies only what it claims to be looking at (checked against that account's actual
 * access), a category, a description, and any files the person deliberately chose.
 */

export const FEEDBACK_CATEGORIES = [
  "bug",
  "confusing",
  "change",
  "other",
] as const satisfies readonly string[];

/**
 * Kept beside the values so the dialog and the delivered message read from one source.
 *
 * "Request a change" lost the words "in this tool" when the portal became a target: the same four
 * labels now describe a hosted tool, a shell applet, and the portal itself, and a label that names
 * the wrong one of those is worse than a shorter label.
 */
export const FEEDBACK_CATEGORY_LABELS: Record<(typeof FEEDBACK_CATEGORIES)[number], string> = {
  bug: "Something is broken",
  confusing: "Something is confusing",
  change: "Request a change",
  other: "Other",
};

/**
 * The sign-in name when it is an address someone can actually answer, otherwise null.
 *
 * Since ADR-0042 an account's sign-in name *is* their email address, so for anyone invited under
 * that decision this is simply their address. Accounts that predate it — `ava.acme`, `liam.shared`,
 * the consultant login — sign in with a bare username and have no address recorded anywhere: the
 * `users` table has no email column, by deliberate data minimisation. For those the message carries
 * no `Reply-To`, and both the review step and the email say so rather than implying a reply will
 * arrive somewhere.
 *
 * It lives here, in shared code, because the review step promises the person exactly what the
 * Worker will do. A second rule in the browser — "does it contain an @" — would eventually promise
 * a reply the server then did not enable. `InviteEmailSchema` is reused rather than re-derived so
 * "is this an address we can reach" has one definition: the same one that decides which addresses
 * may become accounts.
 */
export function replyToAddress(username: string): string | null {
  return InviteEmailSchema.safeParse(username).success ? username : null;
}

/** How a file size is written wherever one is shown: the picker, the review step, the email. */
export function formatFileSize(byteCount: number): string {
  if (byteCount < 1024) return `${byteCount} B`;
  if (byteCount < 1024 * 1024) return `${Math.round(byteCount / 1024)} KB`;
  return `${(byteCount / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The bounds on a subject an older dialog sends. The dialog no longer has a subject field; the
 * ticket is titled from the description instead (`requestTitle`).
 */
export const FEEDBACK_MIN_SUBJECT_CHARACTERS = 3;
export const FEEDBACK_MAX_SUBJECT_CHARACTERS = 120;

export const FEEDBACK_MIN_DESCRIPTION_CHARACTERS = 10;
export const FEEDBACK_MAX_DESCRIPTION_CHARACTERS = 4_000;

export const FEEDBACK_MAX_ATTACHMENTS = 5;
/**
 * Attachments are streamed straight into R2 and never buffered, so neither Cloudflare's 5 MiB
 * message cap nor a Worker's 128 MB memory limit bounds them any more.
 *
 * They travel as their own raw request — `bucket.put(key, request.body)` — rather than as base64
 * inside the submission, so the bytes pass through the Worker without a full copy ever existing in
 * memory. What is left to choose is a sensible ceiling on what one person can put in a bucket for
 * one piece of feedback, which is a cost and abuse question rather than a technical one. These sit
 * well inside any plan's request-body limit.
 */
export const FEEDBACK_MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;
export const FEEDBACK_MAX_ATTACHMENT_TOTAL_BYTES = 150 * 1024 * 1024;
/**
 * The submission body carries identifiers now, not files, so it is tiny. It is bounded anyway
 * because an unbounded JSON body is still an unbounded JSON body.
 */
export const FEEDBACK_MAX_REQUEST_BODY_BYTES = 64 * 1024;

/**
 * A screen recording is bounded by time as well as bytes, because a person watching a timer
 * understands "3 minutes" and cannot see megabytes accumulating. The bitrates are what the
 * recorder asks `MediaRecorder` for; encoders treat them as a target, so the duration is sized to
 * land a maximum-length recording comfortably under the per-file cap:
 * (2,000,000 + 128,000) bits/s ÷ 8 × 180 s ≈ 48 MB < 50 MB. A test holds this arithmetic.
 */
export const FEEDBACK_MAX_RECORDING_SECONDS = 180;
export const FEEDBACK_RECORDING_VIDEO_BITS_PER_SECOND = 2_000_000;
export const FEEDBACK_RECORDING_AUDIO_BITS_PER_SECOND = 128_000;

export const FEEDBACK_MAX_ATTACHMENT_LABEL = formatFileSize(FEEDBACK_MAX_ATTACHMENT_BYTES);
export const FEEDBACK_MAX_ATTACHMENT_TOTAL_LABEL = formatFileSize(
  FEEDBACK_MAX_ATTACHMENT_TOTAL_BYTES,
);

/**
 * Every accepted type is one whose bytes can be verified against a signature. A type that could
 * only be taken on the sender's word — `text/plain`, `text/csv` — is deliberately absent: this
 * boundary streams its content into a bucket that a public download route then serves, and "the
 * client said so" is not a standard the rest of this Worker applies to bytes.
 *
 * The two video types exist for screen recordings: WebM is what Chromium and Firefox
 * `MediaRecorder` produce, MP4 is Safari's. Both open the picker to hand-chosen video files too,
 * which is accepted rather than fought — the Worker cannot tell a recording from a file, and a
 * screen capture someone edited or re-exported is still their evidence.
 *
 * The label sits beside the type so every sentence that lists what is accepted derives from this
 * table — a hand-written list in the dialog once outlived two changes to it.
 *
 * The extension is derived from the verified type rather than read off the supplied filename,
 * which removes the dangerous-extension problem instead of blocklisting it.
 */
const ATTACHMENT_TYPES = {
  "image/png": { extension: "png", label: "PNG" },
  "image/jpeg": { extension: "jpg", label: "JPEG" },
  "image/webp": { extension: "webp", label: "WebP" },
  "image/gif": { extension: "gif", label: "GIF" },
  "application/pdf": { extension: "pdf", label: "PDF" },
  "video/webm": { extension: "webm", label: "WebM" },
  "video/mp4": { extension: "mp4", label: "MP4" },
} as const;

export type FeedbackAttachmentMediaType = keyof typeof ATTACHMENT_TYPES;

export const FEEDBACK_ATTACHMENT_MEDIA_TYPES = Object.keys(
  ATTACHMENT_TYPES,
) as readonly FeedbackAttachmentMediaType[];

/** Includes `jpeg` beside the canonical `jpg` so a `.jpeg` upload is not doubled up. */
const KNOWN_EXTENSIONS = new Set<string>([
  ...Object.values(ATTACHMENT_TYPES).map((type) => type.extension),
  "jpeg",
]);

export const FEEDBACK_ATTACHMENT_ACCEPT = Object.keys(ATTACHMENT_TYPES).join(",");

/** "PNG, JPEG, …, or MP4" — the one sentence-ready list every surface quotes. */
export const FEEDBACK_ATTACHMENT_TYPES_LABEL = (() => {
  const labels = Object.values(ATTACHMENT_TYPES).map((type) => type.label);
  return `${labels.slice(0, -1).join(", ")}, or ${labels.at(-1)}`;
})();

export function feedbackAttachmentExtension(mediaType: FeedbackAttachmentMediaType): string {
  return ATTACHMENT_TYPES[mediaType].extension;
}

export function isVideoAttachmentMediaType(mediaType: FeedbackAttachmentMediaType): boolean {
  return mediaType.startsWith("video/");
}

export function parseAttachmentMediaType(
  value: string | null | undefined,
): FeedbackAttachmentMediaType | null {
  const normalized = value?.split(";", 1)[0]?.trim().toLowerCase();
  return normalized && normalized in ATTACHMENT_TYPES
    ? (normalized as FeedbackAttachmentMediaType)
    : null;
}

function matchesBytes(bytes: Uint8Array, offset: number, signature: readonly number[]): boolean {
  return signature.every((value, index) => bytes[offset + index] === value);
}

const SIGNATURES: Record<FeedbackAttachmentMediaType, (bytes: Uint8Array) => boolean> = {
  "image/png": (bytes) => matchesBytes(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  "image/jpeg": (bytes) => matchesBytes(bytes, 0, [0xff, 0xd8, 0xff]),
  "image/webp": (bytes) =>
    matchesBytes(bytes, 0, [0x52, 0x49, 0x46, 0x46]) &&
    matchesBytes(bytes, 8, [0x57, 0x45, 0x42, 0x50]),
  "image/gif": (bytes) =>
    matchesBytes(bytes, 0, [0x47, 0x49, 0x46, 0x38]) &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61,
  "application/pdf": (bytes) => matchesBytes(bytes, 0, [0x25, 0x50, 0x44, 0x46, 0x2d]),
  // WebM is an EBML document; the four-byte EBML magic opens every one.
  "video/webm": (bytes) => matchesBytes(bytes, 0, [0x1a, 0x45, 0xdf, 0xa3]),
  // ISO BMFF: a size prefix, then "ftyp". The brand after it varies; the box name does not.
  "video/mp4": (bytes) => matchesBytes(bytes, 4, [0x66, 0x74, 0x79, 0x70]),
};

/**
 * The bytes an upload must actually begin with, checked against the type it claims to be.
 *
 * Only the first twelve bytes are ever inspected, which is what lets the check run on the leading
 * chunk of a stream rather than on a buffered file.
 */
export function matchesAttachmentSignature(
  mediaType: FeedbackAttachmentMediaType,
  bytes: Uint8Array,
): boolean {
  return SIGNATURES[mediaType](bytes);
}

export const FEEDBACK_SIGNATURE_BYTES = 12;

/**
 * The person's own name for the file. Only the base name survives: any directory component is
 * dropped, control and quoting characters are removed so the value cannot break a MIME or
 * Content-Disposition header, and the result is bounded.
 *
 * Idempotent, because it runs on both sides of the wire and more than once on each.
 */
export function sanitizeFeedbackFilename(value: string): string {
  const base = value.split(/[/\\]/u).pop() ?? "";
  return (
    base
      // eslint-disable-next-line no-control-regex -- control characters are exactly what must go
      .replace(/[\u0000-\u001f\u007f"\\<>:;,]/gu, "")
      .trim()
      .slice(0, 64)
  );
}

/**
 * Removes a trailing extension, but only one this boundary recognises, so a name that merely
 * contains dots — a screenshot's timestamp — keeps all of them. Idempotent: once the extension is
 * gone nothing matches.
 */
export function stripAttachmentExtension(value: string): string {
  const match = /\.([A-Za-z0-9]+)$/u.exec(value);
  const extension = match?.[1]?.toLowerCase();
  if (!match || !extension || !KNOWN_EXTENSIONS.has(extension)) return value;
  return value.slice(0, match.index);
}

/** What an upload reports back, and what a submission then refers to. */
export const FeedbackAttachmentUploadedSchema = z.strictObject({
  attachmentId: OpaqueIdSchema,
  filename: z.string().min(1).max(96),
  byteCount: z.number().int().positive(),
});

export const FeedbackAttachmentMediaTypeSchema = z.enum(
  Object.keys(ATTACHMENT_TYPES) as [FeedbackAttachmentMediaType, ...FeedbackAttachmentMediaType[]],
);

export const FeedbackCategorySchema = z.enum(FEEDBACK_CATEGORIES);

/**
 * What the feedback is about.
 *
 * Two things in the tools list are not the same kind of thing. A **tool** is a private User Worker
 * with its own instance and deployed artifact hash, hosted in a cross-origin frame. An **applet**
 * — Tool Mockup Builder — is a route inside the shell itself, authorized by a permission rather
 * than a per-instance grant, and it has no artifact of its own because it ships with the platform.
 *
 * A **portal** request is about the shell itself — the dashboard, workspace settings, the audit
 * trail, an account page. It names no tool and no applet because there is none: what it is about is
 * the platform, and the only thing left to authorize is that the person is actually in the
 * workspace they say they are reporting from. Like an applet it carries the platform's own version,
 * because the portal *is* the platform.
 *
 * A discriminated union rather than optional fields, so neither the type system nor the parser ever
 * admits "both" or "neither", and each branch carries exactly the identifier its own authorization
 * check needs. An applet and a portal request both name their workspace because, unlike an instance
 * id, neither implies one — that is a claim the Worker verifies, not context it trusts.
 */
export const FeedbackTargetSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("tool"), appInstanceId: OpaqueIdSchema }),
  z.strictObject({
    kind: z.literal("applet"),
    appletKey: z
      .string()
      .min(3)
      .max(80)
      .regex(/^[a-z0-9-]+$/u),
    organizationId: OpaqueIdSchema,
  }),
  z.strictObject({ kind: z.literal("portal"), organizationId: OpaqueIdSchema }),
]);

/** What the audit trail records in `subject_key` for a portal request, which names nothing else. */
export const FEEDBACK_PORTAL_SUBJECT_KEY = "portal";

export const FeedbackSubmissionSchema = z.strictObject({
  target: FeedbackTargetSchema,
  category: FeedbackCategorySchema,
  /**
   * Only from a dialog opened before the subject field was removed (ADR-0089). The dialog now asks
   * only what happened, and the ticket is titled from that (`requestTitle`); a subject an older
   * dialog still sends is the person's own words, so it is used as written.
   */
  subject: z
    .string()
    .trim()
    .min(FEEDBACK_MIN_SUBJECT_CHARACTERS)
    .max(FEEDBACK_MAX_SUBJECT_CHARACTERS)
    .optional(),
  description: z
    .string()
    .trim()
    .min(FEEDBACK_MIN_DESCRIPTION_CHARACTERS)
    .max(FEEDBACK_MAX_DESCRIPTION_CHARACTERS),
  /**
   * Where in the shell the person was when they opened the dialog. The shell hosts a tool in a
   * cross-origin frame and cannot read that frame's URL, so this is the shell's own route — the
   * page the tool was opened at, not necessarily the screen currently inside it. It is presented
   * to the reader in exactly those terms.
   */
  shellPath: WorkspaceShellPathSchema,
  /**
   * Identifiers of files already streamed to R2, not the files themselves. The Worker checks each
   * one belongs to this account and has not been used before.
   */
  attachmentIds: z.array(OpaqueIdSchema).max(FEEDBACK_MAX_ATTACHMENTS).optional(),
});
/**
 * Reports only that the message was accepted and delivered. Unlike an invitation there is no
 * artefact to hand back: a failure is reported as an error the person can retry, not as a status
 * on a successful response.
 */
export const FeedbackSubmittedSchema = z.strictObject({
  target: FeedbackTargetSchema,
  category: FeedbackCategorySchema,
  submittedAt: z.number().int().positive(),
  /**
   * The request identifier this submission was delivered under: the same value the message carries
   * to support, the audit trail records, and the structured log names.
   *
   * It is handed back so the person can quote one identifier that reaches the actual record of what
   * they sent. It cannot appear in the review step, because it does not exist until the request
   * that generates it — so the review step promises it rather than showing it.
   */
  reference: z.string().min(1).max(120),
});

export type FeedbackAttachmentUploaded = z.infer<typeof FeedbackAttachmentUploadedSchema>;
export type FeedbackTarget = z.infer<typeof FeedbackTargetSchema>;
export type FeedbackCategory = z.infer<typeof FeedbackCategorySchema>;
export type FeedbackSubmission = z.infer<typeof FeedbackSubmissionSchema>;
export type FeedbackSubmitted = z.infer<typeof FeedbackSubmittedSchema>;
